import PropTypes from 'prop-types';
import React, { useState } from 'react';

// Presentational migrate/undo banner used by OffersTab and ManageOffers. Three
// states: undo note (migrated), promo card (migratable), small nudge (dismissed).
// Dismiss is remembered per-browser, globally, so the promo is only pitched once.
const DISMISS_KEY = 'sl-offers-v3-migration-dismissed';

const isDismissed = () => {
  if (typeof window === 'undefined') return false;
  return window.localStorage.getItem(DISMISS_KEY) === '1';
};

export const noteStyle = {
  display: 'flex',
  alignItems: 'center',
  gap: '6px',
  marginBottom: '12px',
  fontSize: '14px',
  color: 'var(--sl-text-secondary)',
};

export const promoStyle = {
  display: 'flex',
  alignItems: 'flex-start',
  gap: '12px',
  padding: '16px',
  marginBottom: '16px',
  borderRadius: '8px',
  border: '1px solid var(--sl-border-secondary)',
  background: 'var(--sl-bg-secondary)',
  fontSize: '14px',
};

export const linkButtonStyle = {
  background: 'none',
  border: 'none',
  padding: 0,
  color: 'var(--sl-text-brand)',
  fontWeight: 600,
  cursor: 'pointer',
};

const OffersV3MigrationBanner = ({ show, mode, busy, onMigrate, onUndo }) => {
  const [dismissed, setDismissed] = useState(isDismissed);

  if (!show) return null;

  const dismiss = () => {
    if (typeof window !== 'undefined') window.localStorage.setItem(DISMISS_KEY, '1');
    setDismissed(true);
  };

  if (mode === 'undo') {
    return (
      <div className="offers-v3-migration-banner" data-testid="offers-v3-migration-banner" style={noteStyle}>
        <span>These offers were migrated to V3. Your original V2 offers are saved.</span>
        <button type="button" style={linkButtonStyle} onClick={onUndo} disabled={busy}>
          {busy ? 'Undoing…' : 'Undo migration'}
        </button>
      </div>
    );
  }

  if (dismissed) {
    return (
      <div className="offers-v3-migration-banner" data-testid="offers-v3-migration-banner" style={noteStyle}>
        <span>This event still has V2 offers.</span>
        <button type="button" style={linkButtonStyle} onClick={onMigrate} disabled={busy}>
          {busy ? 'Migrating…' : 'Migrate to V3'}
        </button>
      </div>
    );
  }

  return (
    <div className="offers-v3-migration-banner" data-testid="offers-v3-migration-banner" style={promoStyle}>
      <i className="far fa-info-circle" style={{ color: 'var(--sl-text-brand)', marginTop: '2px' }} aria-hidden="true" />
      <div>
        <div style={{ fontWeight: 600, marginBottom: '4px' }}>A better way to work with offers</div>
        <div style={{ color: 'var(--sl-text-secondary)', marginBottom: '12px' }}>
          Migrate this event&rsquo;s offers to V3 to get reusable deal templates, auto-saved edits, and a
          budget that stays linked to your settlement. Your original offers are kept, so you can undo this
          at any time.
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
          <button type="button" className="btn btn-primary" onClick={onMigrate} disabled={busy}>
            {busy ? 'Migrating…' : 'Migrate to V3'}
          </button>
          <button type="button" data-testid="offers-v3-migration-dismiss" style={linkButtonStyle} onClick={dismiss} disabled={busy}>
            Dismiss
          </button>
        </div>
      </div>
    </div>
  );
};

OffersV3MigrationBanner.propTypes = {
  show: PropTypes.bool,
  mode: PropTypes.oneOf(['migrate', 'undo']),
  busy: PropTypes.bool,
  onMigrate: PropTypes.func,
  onUndo: PropTypes.func,
};

export default OffersV3MigrationBanner;
